/**
 * Authentication context for managing user authentication state.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserResponse, login as apiLogin, logout as apiLogout, getCurrentUser } from '../api/auth';

interface AuthContextType {
  user: UserResponse | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check if user is authenticated on mount
  // SEC-003: Check authentication via API call (cookies are sent automatically)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getCurrentUser();
        setUser(userData);
      } catch (error) {
        // Not authenticated or token invalid
        setUser(null);
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<void> => {
    try {
      // SEC-003: Token is set as httpOnly cookie by backend, no need to store in localStorage
      await apiLogin({ email, password });
      // CODE-007: Clear CSRF token cache after login to ensure fresh token for new session
      const { clearCsrfToken } = await import('../api/csrf');
      clearCsrfToken();
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      // Re-throw the error so the LoginPage can handle it
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    // CODE-007: Clear CSRF token cache on logout to prevent stale token reuse
    const { clearCsrfToken } = await import('../api/csrf');
    clearCsrfToken();
    await apiLogout();
    setUser(null);
  };

  const refreshUser = async (): Promise<void> => {
    // SEC-003: Check authentication via API call (cookies are sent automatically)
    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error) {
      // Not authenticated or token invalid
      setUser(null);
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

